import { DeleteTwoTone, EditTwoTone, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Modal, Popconfirm, Space } from 'antd';
import Title from 'antd/lib/typography/Title';
import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import uuid from 'react-uuid';
import { useLocalStorage } from '../../../helpers/use-local-storage';

const { TextArea } = Input;

interface AppointmentNote {
  id: string;
  appointmentId: string;
  text: string;
}

interface AppointmentNotesProps {
  appointmentId: string;
}

const APPOINTMENT_NOTES_KEY = 'APPOINTMENT_NOTES_KEY';

const getAppointmentNotesStorageKey = (appointmentId: string) =>
  `${APPOINTMENT_NOTES_KEY}.${appointmentId}`;

/**
 * @deprecated - This component is going to be removed once the applications go live
 */
export const AppointmentNotes: FC<AppointmentNotesProps> = ({ appointmentId }) => {
  const storageKey = useMemo(() => getAppointmentNotesStorageKey(appointmentId), [appointmentId]);

  const { formatMessage } = useIntl();

  const [appointmentNotes, setAppointmentNotes] = useLocalStorage<AppointmentNote[]>(
    storageKey,
    [],
  );

  const [selectedNoteId, setSelectedNoteId] = useState<string>();

  const selectedNote = useMemo(
    () => appointmentNotes.find((appointmentNote) => appointmentNote.id === selectedNoteId),
    [appointmentNotes, selectedNoteId],
  );

  const addNote = () => {
    const noteId = uuid();
    setAppointmentNotes((old) => [...old, { id: noteId, appointmentId, text: '' }]);
    setSelectedNoteId(noteId);
  };

  const deleteNote = (noteToDeleteId: string) => {
    setAppointmentNotes((old) => old.filter((note) => note.id !== noteToDeleteId));
  };

  const saveNote = (content: string | undefined) => {
    setSelectedNoteId(undefined);
    if (selectedNote) {
      setAppointmentNotes((prev) =>
        prev.map((note) =>
          note.id !== selectedNote.id ? note : { ...selectedNote, text: content ?? '' },
        ),
      );
    }
  };

  return (
    <div>
      <Title level={3} style={{ fontWeight: 'bold' }}>
        {formatMessage({ id: 'appointment.notes' })}
      </Title>
      <Space style={{ width: '100%' }} direction={'vertical'}>
        {appointmentNotes.map((appointmentNote, index) => {
          return (
            <Card key={appointmentNote.id}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', justifyItems: 'center' }}
              >
                <div>
                  <div style={{ fontWeight: 'bolder' }}>{`${formatMessage({
                    id: 'appointment.note',
                  })} (${index + 1})`}</div>
                  <div style={{ color: 'darkgray' }}>{appointmentNote.text}</div>
                </div>
                <Space>
                  <Button
                    icon={<EditTwoTone />}
                    type={'text'}
                    onClick={() => setSelectedNoteId(appointmentNote.id)}
                  />

                  <Popconfirm
                    title={formatMessage({ id: 'appointment.delete-note.are-you-sure' })}
                    onConfirm={() => deleteNote(appointmentNote.id)}
                  >
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      icon={<DeleteTwoTone twoToneColor={'#f00'} />}
                      type={'text'}
                    />
                  </Popconfirm>
                </Space>
              </div>
            </Card>
          );
        })}
        <Button icon={<PlusOutlined />} onClick={addNote} block type={'dashed'}>
          {formatMessage({ id: 'appointment.add-note' })}
        </Button>
      </Space>
      <AppointmentNoteModal
        open={!!selectedNoteId}
        initialValue={selectedNote?.text}
        onClose={() => {
          setSelectedNoteId(undefined);
        }}
        onSave={saveNote}
      />
    </div>
  );
};

const AppointmentNoteModal: FC<{
  initialValue: string | undefined;
  open: boolean;
  onClose: () => void;
  onSave: (noteContent: string | undefined) => void;
}> = ({ initialValue, onSave, open, onClose }) => {
  const [internalValue, setInternalValue] = useState<string>();

  useEffect(() => {
    if (open) {
      setInternalValue(initialValue);
    }
  }, [open, initialValue]);

  const { formatMessage } = useIntl();

  return (
    <Modal
      title={formatMessage({ id: 'appointment.edit-note' })}
      footer={
        <Space>
          <Button size={'small'} onClick={() => onClose()}>
            {formatMessage({ id: 'action.cancel' })}
          </Button>
          <Button size={'small'} type={'primary'} onClick={() => onSave(internalValue)}>
            {formatMessage({ id: 'action.confirm' })}
          </Button>
        </Space>
      }
      onCancel={onClose}
      open={!!open}
    >
      <Form layout={'vertical'}>
        <Form.Item label={formatMessage({ id: 'appointment.note' })}>
          <TextArea
            value={internalValue || ''}
            onChange={(e) => setInternalValue(e.target.value)}
            placeholder={formatMessage({ id: 'appointment.enter-note' })}
            autoSize={{ minRows: 3, maxRows: 5 }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
